<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { useHead } from '@unhead/vue'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import { Environment } from '@xcel-next/core/enums'

import { useConfigStore } from '../../stores/config'
import EventBus from '../../event-bus/EventBus'

import type { ComposedPage } from '@xcel-next/composer-schema'
import type { RsConfig } from '../../stores/config'
import type { Brand } from '@xcel-next/core/enums'

type AgfMetaData = {
  type: 'static',
  assetid?: string,
  section: string,
  segA: string,
  segB: string,
  segC: string,
  subbrand?: string
}

type NielsenSdkInstance = {
  ggPM: (event: string, data: AgfMetaData) => void
}

declare global {
  interface Window {
    NOLBUNDLE?: {
      nlsQ: (confId: string, name: string, options?: Record<string, string>) => NielsenSdkInstance
    }
  }
}

const props = defineProps<{
  pageData: ComposedPage,
  brand?: Brand
}>()

const { rsConfig } = storeToRefs(useConfigStore())

const nSdkInstance = ref<NielsenSdkInstance | null>(null)
const latestAgfMetaData = ref<AgfMetaData>({ type: 'static', section: '', segA: '', segB: 'CPI', segC: 'UNKNOWN' })

// just a shortcut to the AGF config object
const agfConfig = computed<RsConfig['agfConfig']>(() => rsConfig.value.agfConfig)

// ensure that all necessary AGF config values are set
const isAgfConfigValid = computed(() =>
  !!agfConfig.value?.vcid?.trim() &&
  Object.values(agfConfig.value?.appId ?? {}).every(value => (value ?? '').trim() !== '') &&
  !!agfConfig.value?.segAPrefix?.trim()
)

// ensure that agf measurement is only acivated while config is enabled and valid and we are not in development mode
const agfActivated = computed(() => !!agfConfig.value?.enabled && isAgfConfigValid.value && process.env.NODE_ENV !== Environment.development)

// create a path string from the mainStructurePath array
const section = computed(() => {
  const mainStructurePath = props.pageData.body.mainStructurePath ?? []
  const path = mainStructurePath.length === 1
    ? mainStructurePath[0]?.url?.replace('/', '') || '' // some urls only have 1 main structure path (e.g. epg broadcast or vod urls)
    : mainStructurePath
      .slice(1) // remove first element ('Home')
      .map(item => item?.url?.replace('/', '')) // clean URL if available
      .filter((url): url is string => !!url) // remove empty and undefined values
      .join('_') // concatenate with '_'

  // set brand name as prefix
  return (props.brand ?? '') + (path ? `_${path}` : '')
})

// generate the segA value for the AGF measurement based on the IVWTag logic
const segA = computed(() => `${agfConfig.value?.segAPrefix ?? ''}_${props.pageData.metaData?.IVWTag ?? ''}`)

/*
  How do I ensure functionality?
  ------------------------------
  Filter HTTP traffic with the string 'nmr' to capture traffic from the SDK.
  This will capture traffic going to Nielsen servers.
*/
useHead({
  script: computed(() => agfActivated.value
    ? [{
      'data-vmid': 'agf-static-queue-snippet',
      key: 'agf-static-queue-snippet',
      type: 'text/javascript',
      async: false,
      innerHTML: `
                !(function (e, n) {
                  function t (e) {
                    return typeof e === 'object' ? JSON.parse(JSON.stringify(e)) : e
                  }
                  e[n] = e[n] || {
                    nlsQ: function (o, r, c) {
                      const s = e.document
                      const a = s.createElement('script')
                      a.async = 1,
                      a.src = (e.location.protocol === 'http:' ? 'http:' : 'https:') + '//cdn-gl.nmrodam.com/conf/' + o + '.js#name=' + r + '&ns=' + n
                      const i = s.getElementsByTagName('script')[0]
                      return i.parentNode.insertBefore(a, i),
                      e[n][r] = e[n][r] || {
                        g: c || {},
                        ggPM: function (o, c, s, a, i) { e[n][r].q = e[n][r].q || []; try { const l = t([o, c, s, a, i]); e[n][r].q.push(l) } catch (e) { console && console.log && console.log('Error: Cannot register event in Nielsen SDK queue.') } },
                        trackEvent: function (o) { e[n][r].te = e[n][r].te || []; try { const c = t(o); e[n][r].te.push(c) } catch (e) { console && console.log && console.log('Error: Cannot register event in Nielsen SDK queue.') } }
                      },
                      e[n][r]
                    }
                  }
                }(window, 'NOLBUNDLE'))
              `
    }]
    : [])
})

// we'll save the latest metadata for the page to terminate the measurement of the previous page within 'staticend' event
const agfMetaData = (segB = 'CPI'): AgfMetaData => {
  latestAgfMetaData.value = {
    type: 'static',
    assetid: props.pageData.pageMeta?.id,
    section: section.value,
    segA: segA.value, // equal to old IVW 'cp' value
    segB, // 'CPI' for Content || 'WPI' for 'Werbetraeger' (after user interaction with the site and newly loaded Content)
    segC: 'UNKNOWN', // meant is an identifier for the program brand, currently still Default UNKNOWN, possibly relevant in the future
    subbrand: agfConfig.value?.vcid
  }
  // try to remember the latest metadata for the page for the back-forward cache handling in a special form
  window.localStorage.setItem('agfMetaData', JSON.stringify(latestAgfMetaData.value))
  // and return the metadata for the current page 'staticstart' event
  return latestAgfMetaData.value
}

const triggerAdvertisingMedium = () => {
  if (agfActivated.value) {
    // Stop static measurement for the page => Event 'staticend' Call
    nSdkInstance.value?.ggPM('staticend', latestAgfMetaData.value)
    // Start static measurement for the advertising medium after user interaction (new metadata) => Event 'staticstart' Call
    nSdkInstance.value?.ggPM('staticstart', agfMetaData('WPI'))
  }
}

const toggleContentTracking = (stop: boolean) => {
  if (agfActivated.value) {
    stop
      ? nSdkInstance.value?.ggPM('staticend', latestAgfMetaData.value)
      : nSdkInstance.value?.ggPM('staticstart', agfMetaData())
  }
}

// EventBus callbacks are untyped (...args: unknown[]) - narrow instead of casting, keep the reference stable for $off
const handleToggleContentTracking = (...args: unknown[]) => {
  if (typeof args[0] === 'boolean') toggleContentTracking(args[0])
}

const backForwardCacheHandler = () => {
  // use pageshow event to detect if the page was restored from the bfcache
  window.addEventListener('pageshow', (event) => {
    // if the page was restored from the bfcache
    if (event.persisted) {
      // get the latest metadata from the local storage
      const storedMetaData = window.localStorage.getItem('agfMetaData')
      if (storedMetaData) latestAgfMetaData.value = JSON.parse(storedMetaData)
      // terminate the measurement of the previous page
      nSdkInstance.value?.ggPM('staticend', latestAgfMetaData.value)
      // start the measurement of the new page loaded from bfcache
      nSdkInstance.value?.ggPM('staticstart', agfMetaData())
    }
  })
}

onMounted(() => {
  if (agfActivated.value) {
    // debug option disabled by default - enable if you need some noise in the browser console
    const enableDebugging = false
    // prevent debug output in staging and production environment
    const isNonProdEnv = process.env.NODE_ENV === Environment.development || process.env.NODE_ENV === Environment.testing
    const debugOptions: Record<string, string> = enableDebugging && isNonProdEnv ? { nol_sdkDebug: 'debug' } : {}
    // SDK Initialization
    nSdkInstance.value = window.NOLBUNDLE!.nlsQ(agfConfig.value!.appId.static!, 'nlsnInstance', debugOptions)
    // Event 'staticstart' Call
    nSdkInstance.value.ggPM('staticstart', agfMetaData())
    // Handle back-forward cache of the browser and start the measurement of the new page loaded from bfcache
    backForwardCacheHandler()
  }
  // listen to global event
  EventBus.$on('triggerAGF', triggerAdvertisingMedium)
  EventBus.$on('toggleAGF', handleToggleContentTracking)
})

onBeforeUnmount(() => {
  // remove global event listener
  EventBus.$off('triggerAGF', triggerAdvertisingMedium)
  EventBus.$off('toggleAGF', handleToggleContentTracking)
})
</script>

<template>
  <span />
</template>
